import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import useAuth from '../../auth/useAuth';

import Layout from '../UI/introGameLayout/layout';
import QuestionsTransactionsTable from '../UI/questionsTransactionsTable';

import {
  getGamesSessions,
  decisionsInsigtsQuery,
  itemsQuery,
  gamesQuery,
  scenarioLevelsQuery,
  decisionsQuery
} from './api';

import { 
  popupMessagesSummaryDecisionBreakdownAccounts,
  summaryDecisionBreakdownElementsHeaders,
  summaryDecisionBreakdownElementsToDiplay
} from './data';

import PopupMessages from '../UI/introGameLayout/popupMessages';

import './styles.css';

export default function BalancedEquationSummaryDecisionBreakdownAccounts() {
  const navigate = useNavigate();
  
  const { 
    getUser,
    getCurrentBalancedEquationGameId
  } = useAuth();

  const user = getUser();
  
  const gameId = getCurrentBalancedEquationGameId();

  const [scenarioId, setScenarioId] = useState(0);
  const [currentGameSessionId, setCurrentGameSessionId] = useState(0);
  const [isFirstGameOfTheType, setIsFirstGameOfTheType] = useState(0);

  const [elementsTableData, setElementsTableData] = useState([]);
  const [dataLoaded, setDataLoaded] = useState(false);

  const { data: gamesSessions } = useQuery({
    queryKey: ['gamesSessionsSummaryBreakdown'],
    queryFn: getGamesSessions,
    refetchOnWindowFocus: false,
  });

  const { data: games } = useQuery(gamesQuery()) || {};
  const { data: scenarioLevels } = useQuery(scenarioLevelsQuery({scenarioId}));
  const { data: decisionsInsights } = useQuery(decisionsInsigtsQuery({gameId, gameSessionId: currentGameSessionId}));
  const { data: items } = useQuery(itemsQuery());
  const { data: decisions } = useQuery(decisionsQuery({gameSessionId: currentGameSessionId}));

  useEffect(() => {
    if(gamesSessions && games && gameId && user) {
      if(gamesSessions.filter(
          session =>
            games.find(game => game.id === session.game && game.game_type === 'Balanced Equation')
        ).length < 2) {
        setIsFirstGameOfTheType(true);
      }

      const currentGameSessions = gamesSessions.filter(session => session.user === user?.id && session.game === gameId);
      const currentGameSession = currentGameSessions.find(session => !session.completed);
      
      if (currentGameSession) {
        setCurrentGameSessionId(currentGameSession?.id);
      } else {
        const completedGameSession = gamesSessions
          .filter(session => session.user === user?.id && session.game === gameId && session.completed)
          .reduce((maxSession, session) => (session.id > (maxSession?.id || 0) ? session : maxSession), null);
  
        setCurrentGameSessionId(completedGameSession?.id);
      }
    }
  }, [gamesSessions, games, gameId, user]);

  useEffect(() => {
    if(games) {
      const game = games.find(game => game.game_type.includes('Balanced Equation') && game.id === gameId);

      if(game && game.scenario) {
        setScenarioId(game.scenario);
      }
    }
  }, [games, gameId]);

  useEffect(() => {
    if(scenarioLevels?.length && items && decisions && decisionsInsights) {
      setDataLoaded(true);

      const elementsData = [];
      
      const uniqueDecisionsInsights = decisionsInsights
        .filter(question => 
          question.item_options?.some(itemOption => 
            itemOption.selected_change_options.some(changeOption => 
              changeOption.selected_change !== null
            )
          )
        )
        .reduce((acc, question) => {
          const existingIndex = acc.findIndex(q => q.transaction_id === question.transaction_id);
                
          if (existingIndex === -1) {
            acc.push(question);
          } else if (question.level < acc[existingIndex].level) {
            acc[existingIndex] = question;
          }
                
          return acc;
        }, [])
        .sort((a, b) => a.transaction_id - b.transaction_id);

      const scenarioLevel = scenarioLevels.find(scenario => scenario.design.includes("3"));

      uniqueDecisionsInsights.forEach(decisionInsight => {
        const row = {
          question: decisionInsight.question,
        };
        
        const relatedDecisions = decisions.filter(decision => decision.question === decisionInsight.question_id);
        const lowestGroupId = relatedDecisions.map(item => item.group_id).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))[0];

        const decisionsWithLowestGroupId = relatedDecisions.filter(
          item => item.group_id === lowestGroupId
        );

        decisionInsight.item_options.forEach(decisionInsightItemOption => {
          const itemsWithBothValues = decisionInsightItemOption.selected_change_options.filter(option => option.selected_option && option.selected_change);
          let isCorrect = true;
          itemsWithBothValues.forEach(itemWithBothValues => {
            const selectedOptionRelatedScenarioLevelOption = scenarioLevel.be_options.find(option => option.account_item.name === itemWithBothValues.selected_option);
            const selectedChangeRelatedScenarioLevelOption = scenarioLevel.be_options.find(option => option.account_item.name === itemWithBothValues.selected_change);

            const selectedOptionRelatedScenarioLevelOptionDecision = decisionsWithLowestGroupId.find(decision => decision.selected_option === selectedOptionRelatedScenarioLevelOption.id);
            const selectedChangeRelatedScenarioLevelOptionDecision = decisionsWithLowestGroupId.find(decision => decision.selected_option === selectedChangeRelatedScenarioLevelOption.id);
            isCorrect = selectedOptionRelatedScenarioLevelOptionDecision?.is_correct && selectedChangeRelatedScenarioLevelOptionDecision?.is_correct;
            const value = `${itemWithBothValues.selected_change} ${itemWithBothValues.selected_option}`;
            
            const optionItemName = decisionInsightItemOption.name;
            if (!summaryDecisionBreakdownElementsToDiplay.includes(optionItemName)) {
              if (row[optionItemName]) {
                row[optionItemName].push({
                  value,
                  isCorrect,
                  background: isCorrect ? '#D1E9D6' : '#FBD8D8',
                });
              } else {
                row[optionItemName] = [{
                  value,
                  background: isCorrect ? '#D1E9D6' : '#FBD8D8',
                }];
              }
            }
          });
        });      
        elementsData.push(row);
      });

      setElementsTableData(elementsData);

    }
  }, [scenarioLevels, items, decisions, decisionsInsights]);
   
  const handlePopupMessagesNextButtonClick = () => {
    let redirectPath = '/balanced-equation/summary/final';
    if(isFirstGameOfTheType) {
      redirectPath = '/balanced-equation/summary/feedback';
    }
    navigate(redirectPath);
  }
  
  const handlePopupMessagesPrevButtonClick = () => {
    navigate('/balanced-equation/summary/decision-breakdown');
  }
  
  return(
    <Layout
      backgroundColor="#FBEDD5"
      isBackgroundBlurred={false}
      isHeaderVisible={true}
      isFooterVisible={false}
    >
      {
        dataLoaded &&
          <div className="game-summary-container game-summary-decision-questions-transactions-container">
            <PopupMessages
              messages={popupMessagesSummaryDecisionBreakdownAccounts}
              messageSize="xx-small"
              buttonText="Next"
              onButtonClick={handlePopupMessagesNextButtonClick}
              additionalButtonText="Back"
              onAdditionalButtonClick={handlePopupMessagesPrevButtonClick}
              isAvatarOnTop={true}
            />
            <QuestionsTransactionsTable 
              tableColumnHeader="Transactions"
              tableHeaders={summaryDecisionBreakdownElementsHeaders}
              tableData={elementsTableData}
              bigCells={true}
            />
          </div>
      }
    </Layout>
  );
}
